/* eslint-disable @typescript-eslint/no-explicit-any */

declare global {
  interface Window {
    dataLayer?: any[];
    gtag?: (...args: any[]) => void;
    fbq?: (...args: any[]) => void;
    _fbq?: any;
  }
}

let gaInitialized = false;
let gtmInitialized = false;
let metaPixelInitialized = false;

/**
 * Google Analytics 4, Google Tag Manager ve Meta Pixel scriptlerini dinamik yukler.
 * Magaza yonetim panelinden girilen ayarlara gore calisir.
 */
export function initAnalytics(settings?: Record<string, string | null | undefined>) {
  if (!settings || typeof window === 'undefined') return;

  const gaId = settings['analytics.ga_id']?.trim();
  const gtmId = settings['analytics.gtm_id']?.trim();
  const pixelId = settings['analytics.meta_pixel_id']?.trim();

  // 1. Google Analytics 4 (gtag.js)
  if (gaId && !gaInitialized) {
    try {
      const script = document.createElement('script');
      script.async = true;
      script.src = `https://www.googletagmanager.com/gtag/js?id=${gaId}`;
      document.head.appendChild(script);

      window.dataLayer = window.dataLayer || [];
      window.gtag = function () {
        window.dataLayer?.push(arguments);
      };
      window.gtag('js', new Date());
      window.gtag('config', gaId, { send_page_view: false });
      gaInitialized = true;
    } catch (err) {
      console.warn('Google Analytics yuklenemedi:', err);
    }
  }

  // 2. Google Tag Manager (GTM)
  if (gtmId && !gtmInitialized) {
    try {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ 'gtm.start': new Date().getTime(), event: 'gtm.js' });
      const script = document.createElement('script');
      script.async = true;
      script.src = `https://www.googletagmanager.com/gtm.js?id=${gtmId}`;
      document.head.appendChild(script);
      gtmInitialized = true;
    } catch (err) {
      console.warn('GTM yuklenemedi:', err);
    }
  }

  // 3. Meta Pixel
  if (pixelId && !metaPixelInitialized) {
    try {
      (function (f: any, b: any, e: any, v: any, n?: any, t?: any, s?: any) {
        if (f.fbq) return;
        n = f.fbq = function () {
          n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
        };
        if (!f._fbq) f._fbq = n;
        n.push = n;
        n.loaded = !0;
        n.version = '2.0';
        n.queue = [];
        t = b.createElement(e);
        t.async = !0;
        t.src = v;
        s = b.getElementsByTagName(e)[0];
        s.parentNode.insertBefore(t, s);
      })(window, document, 'script', 'https://connect.facebook.net/en_US/fbevents.js');

      window.fbq?.('init', pixelId);
      metaPixelInitialized = true;
    } catch (err) {
      console.warn('Meta Pixel yuklenemedi:', err);
    }
  }
}

/**
 * Sayfa goruntuleme olayi (Visitor funnel adimi)
 */
export function trackPageView(url: string, title?: string) {
  if (typeof window === 'undefined') return;

  // GA4
  if (window.gtag) {
    window.gtag('event', 'page_view', {
      page_location: url,
      page_title: title || document.title,
    });
  }

  // GTM
  if (window.dataLayer) {
    window.dataLayer.push({
      event: 'page_view',
      funnel_step: 'visitor',
      page_location: url,
      page_title: title || document.title,
    });
  }

  // Meta Pixel
  if (window.fbq) {
    window.fbq('track', 'PageView');
  }
}

export interface AnalyticsItem {
  id: string | number;
  name: string;
  price: number;
  quantity?: number;
  category?: string;
  brand?: string;
  variant?: string;
}

/**
 * Urun inceleme olayi (Product view event & funnel adimi)
 */
export function trackViewItem(product: AnalyticsItem) {
  if (typeof window === 'undefined') return;

  const item = {
    item_id: String(product.id),
    item_name: product.name,
    price: product.price,
    item_category: product.category,
    item_brand: product.brand,
    item_variant: product.variant,
    quantity: product.quantity || 1,
  };

  // GA4
  if (window.gtag) {
    window.gtag('event', 'view_item', {
      currency: 'TRY',
      value: product.price,
      items: [item],
    });
  }

  // GTM
  if (window.dataLayer) {
    window.dataLayer.push({
      event: 'view_item',
      funnel_step: 'product',
      ecommerce: {
        currency: 'TRY',
        value: product.price,
        items: [item],
      },
    });
  }

  // Meta Pixel
  if (window.fbq) {
    window.fbq('track', 'ViewContent', {
      content_ids: [String(product.id)],
      content_name: product.name,
      content_category: product.category,
      content_type: 'product',
      value: product.price,
      currency: 'TRY',
    });
  }
}

/**
 * Sepete ekleme olayi (Add to cart event & funnel adimi)
 */
export function trackAddToCart(item: AnalyticsItem) {
  if (typeof window === 'undefined') return;

  const qty = item.quantity || 1;
  const ecommerceItem = {
    item_id: String(item.id),
    item_name: item.name,
    price: item.price,
    item_category: item.category,
    item_brand: item.brand,
    item_variant: item.variant,
    quantity: qty,
  };

  // GA4
  if (window.gtag) {
    window.gtag('event', 'add_to_cart', {
      currency: 'TRY',
      value: item.price * qty,
      items: [ecommerceItem],
    });
  }

  // GTM
  if (window.dataLayer) {
    window.dataLayer.push({
      event: 'add_to_cart',
      funnel_step: 'cart',
      ecommerce: {
        currency: 'TRY',
        value: item.price * qty,
        items: [ecommerceItem],
      },
    });
  }

  // Meta Pixel
  if (window.fbq) {
    window.fbq('track', 'AddToCart', {
      content_ids: [String(item.id)],
      content_name: item.name,
      content_type: 'product',
      value: item.price * qty,
      currency: 'TRY',
    });
  }
}

/**
 * Odeme baslatma olayi (Begin checkout event & funnel adimi)
 */
export function trackBeginCheckout(params: {
  total: number;
  items: AnalyticsItem[];
}) {
  if (typeof window === 'undefined') return;

  const ecommerceItems = params.items.map((i) => ({
    item_id: String(i.id),
    item_name: i.name,
    price: i.price,
    quantity: i.quantity || 1,
    item_category: i.category,
    item_brand: i.brand,
    item_variant: i.variant,
  }));

  // GA4
  if (window.gtag) {
    window.gtag('event', 'begin_checkout', {
      currency: 'TRY',
      value: params.total,
      items: ecommerceItems,
    });
  }

  // GTM
  if (window.dataLayer) {
    window.dataLayer.push({
      event: 'begin_checkout',
      funnel_step: 'checkout',
      ecommerce: {
        currency: 'TRY',
        value: params.total,
        items: ecommerceItems,
      },
    });
  }

  // Meta Pixel
  if (window.fbq) {
    window.fbq('track', 'InitiateCheckout', {
      content_ids: params.items.map((i) => String(i.id)),
      num_items: params.items.reduce((acc, i) => acc + (i.quantity || 1), 0),
      value: params.total,
      currency: 'TRY',
    });
  }
}

/**
 * Satin alma olayi (Purchase conversion event & funnel adimi)
 */
export function trackPurchase(params: {
  orderNumber: string;
  total: number;
  tax?: number;
  shipping?: number;
  items: AnalyticsItem[];
}) {
  if (typeof window === 'undefined') return;

  const ecommerceItems = params.items.map((i) => ({
    item_id: String(i.id),
    item_name: i.name,
    price: i.price,
    quantity: i.quantity || 1,
    item_category: i.category,
    item_brand: i.brand,
    item_variant: i.variant,
  }));

  // GA4
  if (window.gtag) {
    window.gtag('event', 'purchase', {
      transaction_id: params.orderNumber,
      currency: 'TRY',
      value: params.total,
      tax: params.tax,
      shipping: params.shipping,
      items: ecommerceItems,
    });
  }

  // GTM
  if (window.dataLayer) {
    window.dataLayer.push({
      event: 'purchase',
      funnel_step: 'purchase',
      ecommerce: {
        transaction_id: params.orderNumber,
        currency: 'TRY',
        value: params.total,
        tax: params.tax,
        shipping: params.shipping,
        items: ecommerceItems,
      },
    });
  }

  // Meta Pixel
  if (window.fbq) {
    window.fbq('track', 'Purchase', {
      content_ids: params.items.map((i) => String(i.id)),
      content_type: 'product',
      value: params.total,
      currency: 'TRY',
    });
  }
}

/**
 * Arama olayi (Search event)
 */
export function trackSearch(query: string) {
  if (!query || typeof window === 'undefined') return;

  // GA4
  if (window.gtag) {
    window.gtag('event', 'search', {
      search_term: query,
    });
  }

  // GTM
  if (window.dataLayer) {
    window.dataLayer.push({
      event: 'search',
      search_term: query,
    });
  }

  // Meta Pixel
  if (window.fbq) {
    window.fbq('track', 'Search', {
      search_string: query,
    });
  }
}
